// suvee-meal-card.js
// Suvee Meal Card Quantity Display Handler
// Syncs ONLY listing/meal-card quantity inputs from /cart.js (never touches cart drawer/cart page inputs)
(function () {
    'use strict';

    // Guard against multiple initializations
    if (window.SuveeMealCardInitialized) return;
    window.SuveeMealCardInitialized = true;

    /**
     * Roots where "listing" product cards live (collection/search grids).
     * We will only update inputs inside these roots (plus .suvee-meal-card wrappers).
     */
    function getListingRoots() {
        const roots = [
            document.querySelector('#ProductGridContainer'),
            document.querySelector('#product-grid'),
            document.querySelector('.template-search__results'),
            document.querySelector('.collection'),
        ].filter(Boolean);

        return roots;
    }

    function isInListingContext(el) {
        return Boolean(
            el.closest('.suvee-meal-card') ||
            el.closest('#ProductGridContainer') ||
            el.closest('#product-grid') ||
            el.closest('.template-search__results') ||
            el.closest('.collection')
        );
    }

    /**
     * Select ONLY quantity inputs that belong to listing cards (not cart drawer/cart page).
     */
    function getMealCardQuantityInputs() {
        const found = new Set();

        // Always include the custom Suvee card inputs (if present)
        document
            .querySelectorAll(
                '.suvee-meal-card__quantity-input[data-quantity-variant-id], .suvee-meal-card input[data-quantity-variant-id]'
            )
            .forEach((el) => found.add(el));

        // Include Dawn-like meal cards, but only inside listing roots
        const roots = getListingRoots();
        roots.forEach((root) => {
            root
                .querySelectorAll('.card--meal input[data-quantity-variant-id], quick-add-bulk input[data-quantity-variant-id]')
                .forEach((el) => found.add(el));
        });

        return Array.from(found);
    }

    function applyClassesForInput(input) {
        const value = parseInt(input.value, 10) || 0;
        const cartQuantity = parseInt(input.dataset.cartQuantity, 10) || 0;
        const actualValue = Math.max(value, cartQuantity);

        // Suvee structure
        const suveeQtyWrapper = input.closest('.suvee-meal-card__quantity');
        if (suveeQtyWrapper) {
            if (actualValue === 0) {
                suveeQtyWrapper.classList.add('suvee-meal-card__quantity--zero');
                suveeQtyWrapper.classList.remove('suvee-meal-card__quantity--has-items');
            } else {
                suveeQtyWrapper.classList.remove('suvee-meal-card__quantity--zero');
                suveeQtyWrapper.classList.add('suvee-meal-card__quantity--has-items');
            }
            return;
        }

        // Dawn listing structure (search/collection cards)
        const dawnQtyWrapper = input.closest('.quantity.cart-quantity');
        if (dawnQtyWrapper && isInListingContext(dawnQtyWrapper)) {
            if (actualValue === 0) {
                dawnQtyWrapper.classList.add('quantity--zero');
                dawnQtyWrapper.classList.remove('quantity--has-items');
            } else {
                dawnQtyWrapper.classList.remove('quantity--zero');
                dawnQtyWrapper.classList.add('quantity--has-items');
            }
        }
    }

    /**
     * Update CSS classes based on quantity values (listing only)
     */
    function updateQuantityClasses() {
        getMealCardQuantityInputs().forEach(applyClassesForInput);
    }

    /**
     * Fetch cart and sync ONLY listing quantity inputs on page with cart quantities
     * (Never writes to cart drawer/cart page inputs => prevents flicker)
     */
    async function syncQuantitiesFromCart(reason) {
        try {
            const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
            if (!response.ok) return;

            const cart = await response.json();

            // Build a map of variant_id -> quantity from cart
            const cartQuantities = {};
            cart.items.forEach((item) => {
                const variantId = String(item.variant_id);
                cartQuantities[variantId] = (cartQuantities[variantId] || 0) + item.quantity;
            });

            const active = document.activeElement;

            // Update ONLY listing inputs
            getMealCardQuantityInputs().forEach((input) => {
                if (!isInListingContext(input)) return;

                // Don’t overwrite if user is editing this input
                if (active === input) return;

                const variantId = input.dataset.quantityVariantId;
                if (!variantId) return;

                const cartQty = cartQuantities[String(variantId)] || 0;

                const currentValue = parseInt(input.value, 10) || 0;
                const currentCartValue = parseInt(input.dataset.cartQuantity, 10) || 0;

                if (currentValue !== cartQty || currentCartValue !== cartQty) {
                    input.value = String(cartQty);
                    input.setAttribute('value', String(cartQty)); // important for Dawn reset logic
                    input.dataset.cartQuantity = String(cartQty);
                }
            });

            updateQuantityClasses();
        } catch (error) {
            console.error('Error syncing listing quantities from cart:', error);
        }
    }

    /**
     * Scheduler: debounce + single-flight + coalesce
     */
    let syncTimeout = null;
    let inFlight = false;
    let pending = false;

    function requestSync(reason) {
        if (syncTimeout) clearTimeout(syncTimeout);
        // Let Shopify settle cart state after change
        syncTimeout = setTimeout(() => runSync(reason), 300);
    }

    async function runSync(reason) {
        if (inFlight) {
            pending = true;
            return;
        }

        inFlight = true;
        pending = false;

        try {
            await syncQuantitiesFromCart(reason);
        } finally {
            inFlight = false;
            if (pending) {
                pending = false;
                requestSync('pending');
            }
        }
    }

    /**
     * IMPORTANT for Search page:
     * Facets replaces the product grid via AJAX without reliably firing shopify:section:load.
     * So we observe ONLY the product grid container and sync when cards are replaced.
     */
    function observeListingGrid() {
        const root =
            document.querySelector('#ProductGridContainer') ||
            document.querySelector('#product-grid') ||
            document.querySelector('.template-search__results');

        if (!root) return;

        const observer = new MutationObserver((mutations) => {
            let relevant = false;

            for (const m of mutations) {
                if (m.type !== 'childList') continue;

                // Any added node that contains listing quantity inputs means grid was refreshed
                for (const node of m.addedNodes) {
                    if (node.nodeType !== 1) continue;

                    if (
                        (node.matches && node.matches('.card--meal, quick-add-bulk, input[data-quantity-variant-id]')) ||
                        (node.querySelector &&
                            node.querySelector('.card--meal input[data-quantity-variant-id], quick-add-bulk input[data-quantity-variant-id]'))
                    ) {
                        relevant = true;
                        break;
                    }
                }

                if (relevant) break;
            }

            if (relevant) requestSync('grid-mutation');
        });

        observer.observe(root, { childList: true, subtree: true });
    }

    function init() {
        // Initial sync
        requestSync('init');

        // Update classes on local interaction (listing only)
        document.addEventListener(
            'input',
            (e) => {
                const t = e.target;
                if (!(t instanceof HTMLElement)) return;

                if (
                    t.matches('.suvee-meal-card__quantity-input, input[data-quantity-variant-id]') &&
                    isInListingContext(t)
                ) {
                    applyClassesForInput(t);
                }
            },
            true
        );

        document.addEventListener(
            'change',
            (e) => {
                const t = e.target;
                if (!(t instanceof HTMLElement)) return;

                if (
                    t.matches('.suvee-meal-card__quantity-input, input[data-quantity-variant-id]') &&
                    isInListingContext(t)
                ) {
                    applyClassesForInput(t);
                }
            },
            true
        );

        // Dawn PubSub (best signal after cart changes)
        if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
            window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => requestSync('dawn-cartUpdate'));
        }

        // Shopify section re-render (sometimes)
        document.addEventListener('shopify:section:load', () => requestSync('section:load'));

        // Facets / search grid AJAX replacement (reliable for your search template)
        observeListingGrid();

        // Optional app events (safe because we debounce/coalesce)
        document.addEventListener('cart:refresh', () => requestSync('cart:refresh'));
        document.addEventListener('cart:update', () => requestSync('cart:update'));
        document.addEventListener('cart:change', () => requestSync('cart:change'));
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Backup after full load
    window.addEventListener('load', () => setTimeout(() => requestSync('window-load'), 200));

    // Expose for manual triggering
    window.updateMealCardQuantityDisplay = updateQuantityClasses;
    window.syncMealCardQuantitiesFromCart = () => requestSync('manual');
})();
